import React, { useState, useEffect, useRef } from "react";
import { Loader, Icon, Dropdown } from "semantic-ui-react";
import { map } from "lodash";
import moment from "moment";
import { HeaderPage, PrintHeader, PrintTable } from "../../components/Admin";
import { useOrder, usePrintOrientation } from "../../hooks";
import { getBusinessToday, getBusinessDaysAgo } from "../../utils/date";
import {
  buildSalesHeatmap,
  buildMonthlyHeatmap,
  formatHourLabel,
  getHeatColor,
  getHeatGrayColor,
  isDarkCell,
} from "../../utils/heatmap";
import "./PeakHoursReport.scss";

const VIEW_OPTIONS = [
  { key: "weekly", value: "weekly", text: "Semanal" },
  { key: "annual", value: "annual", text: "Anual" },
];

// Ancla temporal, no una fecha real — anterior a cualquier dato posible del
// sistema, para poder pedir "todo el historial" sin necesitar un endpoint
// aparte. Se reemplaza por la fecha real del primer pedido apenas se sabe
// cuál es (ver el segundo useEffect de abajo), nunca queda visible.
const HISTORY_ANCHOR = "2000-01-01";

export function PeakHoursReport() {
  const [dateFrom, setDateFrom] = useState(() => getBusinessDaysAgo(30));
  const [dateTo, setDateTo] = useState(getBusinessToday);
  const [viewMode, setViewMode] = useState("weekly");
  const { loading, orders, getOrdersInRange } = useOrder();
  const hasSnappedToHistory = useRef(false);
  usePrintOrientation("landscape");

  useEffect(() => {
    if (dateFrom && dateTo) getOrdersInRange(dateFrom, dateTo);
  }, [dateFrom, dateTo]);

  // La vista Anual es de estacionalidad acumulada ("qué mes del año suele
  // ser el más fuerte, sumando todos los años que haya") — no una ventana
  // de los últimos 12 meses. La primera vez que se entra a Anual, se pide
  // todo el historial (ancla muy atrás) y, apenas se sabe cuál es el pedido
  // más antiguo real, "Desde" se ajusta a esa fecha — una sola vez en toda
  // la sesión (hasSnappedToHistory), para no pisar un rango que el usuario
  // haya movido a mano después para comparar períodos específicos.
  useEffect(() => {
    if (viewMode === "annual" && !hasSnappedToHistory.current && dateFrom !== HISTORY_ANCHOR) {
      setDateFrom(HISTORY_ANCHOR);
    }
  }, [viewMode]);

  useEffect(() => {
    if (
      viewMode === "annual" &&
      !hasSnappedToHistory.current &&
      dateFrom === HISTORY_ANCHOR &&
      orders?.length
    ) {
      const earliest = moment
        .min(map(orders, (order) => moment.parseZone(order.created_at)))
        .format("YYYY-MM-DD");
      hasSnappedToHistory.current = true;
      setDateFrom(earliest);
    }
  }, [orders]);

  const heatmap = !loading && orders ? buildSalesHeatmap(orders) : null;
  const monthlyHeatmap = !loading && orders ? buildMonthlyHeatmap(orders) : null;
  const activeHeatmap = viewMode === "annual" ? monthlyHeatmap : heatmap;

  const dateRangeLabel = `Del ${moment(dateFrom).format("DD/MM/YYYY")} al ${moment(
    dateTo
  ).format("DD/MM/YYYY")}`;

  return (
    <>
      <div className="screen-only">
        <HeaderPage
          title="Mapa de Calor: Ventas por Día y Franja Horaria"
          btnTitle={
            <>
              <Icon name="print" /> Imprimir
            </>
          }
          btnClick={() => window.print()}
        />
      </div>

      <div
        className="screen-only"
        style={{ display: "flex", gap: "15px", margin: "10px 0 20px" }}
      >
        <div>
          <label style={{ display: "block", marginBottom: 4 }}>Desde</label>
          <input
            type="date"
            value={dateFrom}
            max={dateTo}
            onChange={(e) => setDateFrom(e.target.value)}
          />
        </div>
        <div>
          <label style={{ display: "block", marginBottom: 4 }}>Hasta</label>
          <input
            type="date"
            value={dateTo}
            min={dateFrom}
            onChange={(e) => setDateTo(e.target.value)}
          />
        </div>
        <div>
          <label style={{ display: "block", marginBottom: 4 }}>Vista</label>
          <Dropdown
            selection
            options={VIEW_OPTIONS}
            value={viewMode}
            onChange={(_, data) => setViewMode(data.value)}
          />
        </div>
      </div>

      <PrintTable>
        <PrintHeader
          title={
            viewMode === "annual"
              ? "Mapa de Calor: Ventas por Día y Mes del Año"
              : "Mapa de Calor: Ventas por Día y Franja Horaria"
          }
          subtitle={dateRangeLabel}
        />

        {loading ? (
          <Loader active inline="centered">
            Cargando...
          </Loader>
        ) : !activeHeatmap ? (
          <p>No hay pedidos en el rango de fechas seleccionado.</p>
        ) : viewMode === "annual" ? (
          <div className="print-content">
            <HeatmapGrid
              grid={monthlyHeatmap.grid}
              maxValue={monthlyHeatmap.maxValue}
              columns={monthlyHeatmap.months}
              columnAxisLabel="Mes del Año"
            />
          </div>
        ) : (
          <div className="print-content">
            <HeatmapGrid
              grid={heatmap.grid}
              maxValue={heatmap.maxValue}
              columns={map(heatmap.hours, formatHourLabel)}
              columnAxisLabel="Franja Horaria"
            />
          </div>
        )}
      </PrintTable>
    </>
  );
}

function HeatmapGrid(props) {
  const { grid, maxValue, columns, columnAxisLabel } = props;

  return (
    <div style={{ overflowX: "auto", paddingBottom: 10 }}>
      <div style={{ display: "flex", gap: 15, alignItems: "flex-start", width: "max-content" }}>
        <div style={{ display: "flex" }}>
          <div>
          <table style={{ borderCollapse: "collapse" }}>
            <tbody>
              {map(grid, (row, rowIndex) => (
                <tr key={row.label}>
                  {rowIndex === 0 && (
                    <td
                      rowSpan={grid.length}
                      style={{ position: "relative", width: 26, padding: 0 }}
                    >
                      <div
                        style={{
                          position: "absolute",
                          top: "50%",
                          left: "50%",
                          transform: "translate(-50%, -50%) rotate(-90deg)",
                          whiteSpace: "nowrap",
                          color: "#666",
                          fontSize: "0.85rem",
                          fontWeight: 700,
                        }}
                      >
                        Día de la Semana
                      </div>
                    </td>
                  )}

                  <td
                    style={{
                      padding: "4px 8px",
                      fontWeight: 700,
                      fontSize: "0.8rem",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {row.label}
                  </td>

                  {map(row.values, (value, index) => (
                    <td
                      key={index}
                      className="heatmap-cell"
                      style={{
                        backgroundColor: getHeatColor(value, maxValue),
                        "--print-gray": getHeatGrayColor(value, maxValue),
                        color: isDarkCell(value, maxValue) ? "#fff" : "#444",
                        textAlign: "center",
                        padding: "12px 6px",
                        fontSize: "0.78rem",
                        minWidth: 40,
                        border: "3px solid #fff",
                      }}
                    >
                      {value > 0 ? Math.round(value) : ""}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td></td>
                <td></td>
                {map(columns, (column, index) => (
                  <td
                    key={index}
                    style={{
                      padding: "3px 2px",
                      fontSize: "0.68rem",
                      fontWeight: 700,
                      color: "#666",
                      textAlign: "center",
                    }}
                  >
                    {column}
                  </td>
                ))}
              </tr>
            </tfoot>
          </table>

          <p style={{ textAlign: "center", color: "#666", fontSize: "0.85rem", fontWeight: 700, marginTop: 8 }}>
            {columnAxisLabel}
          </p>
          </div>
        </div>

        <ColorLegend maxValue={maxValue} />
      </div>
    </div>
  );
}

function ColorLegend(props) {
  const { maxValue } = props;
  const steps = 5;
  const ticks = Array.from({ length: steps + 1 }, (_, i) => (maxValue / steps) * (steps - i));
  const gradientColors = ticks.map((value) => getHeatColor(value, maxValue));
  const gradientGrayColors = ticks.map((value) => getHeatGrayColor(value, maxValue));

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 6,
        flexShrink: 0,
      }}
    >
      <div style={{ display: "flex", gap: 8 }}>
        <div
          className="heatmap-legend-bar"
          style={{
            width: 22,
            height: 220,
            borderRadius: 4,
            background: `linear-gradient(to bottom, ${gradientColors.join(", ")})`,
            "--print-gray-gradient": `linear-gradient(to bottom, ${gradientGrayColors.join(
              ", "
            )})`,
          }}
        />
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            height: 220,
            fontSize: "0.75rem",
            fontWeight: 700,
            color: "#666",
          }}
        >
          {map(ticks, (tick, index) => (
            <span key={index}>{Math.round(tick)}</span>
          ))}
        </div>
      </div>

      <div style={{ position: "relative", width: 20, height: 220 }}>
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%) rotate(-90deg)",
            whiteSpace: "nowrap",
            fontSize: "0.8rem",
            fontWeight: 700,
            color: "#666",
          }}
        >
          Ventas Totales (S/)
        </div>
      </div>
    </div>
  );
}
